import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";

import RightDrawer from "components/global/RightDrawer";
import { DataTable } from "components/molecules/table/table";
import { DataTableSkeleton, PageHeader, StatCard } from "components/admin";
import { CrudForm } from "components/admin/forms";
import RefreshActionButton from "components/atoms/refresh-action-button";
import {
  CrudFieldConfig,
  CrudFormRenderProps,
  CrudListRenderProps,
} from "components/admin/forms/types";
import {
  ContentSlot,
  EmbeddedHeaderBar,
  HeaderActions,
  HelperCardSlot,
  PrimaryHeaderButton,
  SecondaryHeaderButton,
  StatsGrid,
} from "components/admin/styles/workspace-crud.styles";
import { getApiErrorMessage } from "utils/admin";

type CrudSectionProps = {
  title: string;
  description?: string;
  data: any[];
  isLoading?: boolean;
  columns: Array<{ label: string; accessor: (row: any) => React.ReactNode }>;
  createLabel?: string;
  drawerTitleCreate?: string;
  drawerTitleEdit?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  rowKey?: string | ((row: any, index: number) => React.Key);
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canRemove?: boolean;
  buildCreateValues?: () => Record<string, any>;
  buildUpdateValues?: (row: any) => Record<string, any>;
  onView?: (row: any) => void;
  onCreate?: (payload: Record<string, any>) => Promise<unknown>;
  onUpdate?: (row: any, payload: Record<string, any>) => Promise<unknown>;
  onRemove?: (row: any) => Promise<unknown>;
  saveSuccessMessage?: string;
  removeSuccessMessage?: string;
  invalidateKeys?: Array<unknown[]>;
  stats?: Array<{ label: string; value: React.ReactNode; helper?: string }>;
  toolbar?: React.ReactNode;
  helperCard?: React.ReactNode;
  /** When "below", render helperCard after the table (e.g. Staff org chart). Default keeps it above. */
  helperCardPlacement?: "above" | "below";
  fields?: CrudFieldConfig[];
  renderForm?: (props: CrudFormRenderProps) => React.ReactNode;
  renderList?: (props: CrudListRenderProps) => React.ReactNode;
  embedded?: boolean;
  hideHeaderActions?: boolean;
  /** When false, skip the page title and right-align actions (useful when a parent tab already names the view). */
  showTitle?: boolean;
};

export type {
  CrudFieldConfig,
  CrudFormRenderProps,
  CrudListRenderProps,
} from "components/admin/forms/types";
export { CrudForm } from "components/admin/forms";

export const CrudSection: React.FC<CrudSectionProps> = ({
  title,
  description,
  data,
  isLoading = false,
  columns,
  createLabel = "Add record",
  drawerTitleCreate,
  drawerTitleEdit,
  emptyTitle = "No records found",
  emptyDescription = "The API has not returned any records for this section yet.",
  rowKey = "id",
  canView = false,
  canCreate = false,
  canEdit = false,
  canRemove = false,
  onView,
  buildCreateValues,
  buildUpdateValues,
  onCreate,
  onUpdate,
  onRemove,
  saveSuccessMessage = "Saved successfully.",
  removeSuccessMessage = "Removed successfully.",
  invalidateKeys = [],
  stats = [],
  toolbar,
  helperCard,
  helperCardPlacement = "above",
  fields,
  renderForm,
  renderList,
  embedded = false,
  hideHeaderActions = false,
  showTitle = true,
}) => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (mode === "edit" && selectedRow && onUpdate) {
        return onUpdate(selectedRow, payload);
      }

      if (mode === "create" && onCreate) {
        return onCreate(payload);
      }

      return null;
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      toast.success(saveSuccessMessage);
      setDrawerOpen(false);
      setSelectedRow(null);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this record."));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (row: any) => {
      if (!onRemove) {
        return null;
      }

      return onRemove(row);
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      toast.success(removeSuccessMessage);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this record."));
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async () =>
      Promise.all(invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))),
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to refresh this view."));
    },
  });

  const openCreateDrawer = () => {
    if (!buildCreateValues) {
      return;
    }

    setMode("create");
    setSelectedRow(null);
    setFormValues(buildCreateValues());
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: any) => {
    if (!buildUpdateValues) {
      return;
    }

    setMode("edit");
    setSelectedRow(row);
    setFormValues(buildUpdateValues(row));
    setDrawerOpen(true);
  };

  const formProps: CrudFormRenderProps = {
    fields,
    initialValues: formValues,
    isSubmitting: saveMutation.isPending,
    onCancel: () => {
      setDrawerOpen(false);
      setSelectedRow(null);
    },
    onSubmit: (payload) => saveMutation.mutate(payload),
    submitLabel: mode === "edit" ? "Save changes" : createLabel,
  };

  const headerActions = hideHeaderActions ? undefined : (
    <HeaderActions>
      <RefreshActionButton
        ButtonComponent={SecondaryHeaderButton}
        isLoading={refreshMutation.isPending}
        label="Refresh"
        loadingLabel="Refreshing..."
        onClick={() => refreshMutation.mutateAsync()}
      />
      {canCreate && buildCreateValues && onCreate ? (
        <PrimaryHeaderButton onClick={openCreateDrawer} type="button">
          <FaPlus size={12} />
          {createLabel}
        </PrimaryHeaderButton>
      ) : null}
    </HeaderActions>
  );

  return (
    <>
      {hideHeaderActions ? null : showTitle ? (
        <PageHeader
          actions={headerActions}
          compact={embedded}
          description={embedded ? undefined : description}
          title={title}
        />
      ) : (
        <EmbeddedHeaderBar>{headerActions}</EmbeddedHeaderBar>
      )}

      {stats.length > 0 ? (
        <StatsGrid $tight={embedded}>
          {stats.map((stat) => (
            <StatCard helper={stat.helper} key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </StatsGrid>
      ) : null}

      {helperCard && helperCardPlacement === "above" ? (
        <HelperCardSlot $tight={embedded}>{helperCard}</HelperCardSlot>
      ) : null}

      <ContentSlot $tight={embedded}>
        {renderList ? (
          renderList({
            canCreate: canCreate && Boolean(buildCreateValues) && Boolean(onCreate),
            canEdit: canEdit && Boolean(onUpdate),
            canRemove: canRemove && Boolean(onRemove),
            canView: canView && Boolean(onView),
            columns,
            createLabel,
            data,
            emptyDescription,
            emptyTitle,
            isLoading,
            isRefreshing: refreshMutation.isPending,
            onCreate: canCreate && buildCreateValues && onCreate ? openCreateDrawer : undefined,
            onRefresh: () => refreshMutation.mutateAsync(),
            onView: canView && onView ? onView : undefined,
            onEdit: canEdit && onUpdate ? openEditDrawer : undefined,
            onRemove:
              canRemove && onRemove
                ? (row) => {
                    if (!row) {
                      return;
                    }

                    if (window.confirm("Remove this record? This action cannot be undone.")) {
                      deleteMutation.mutate(row);
                    }
                  }
                : undefined,
            rowKey,
            toolbar,
          })
        ) : (
          <DataTable
            canView={canView && Boolean(onView)}
            canEdit={canEdit && Boolean(onUpdate)}
            canRemove={canRemove && Boolean(onRemove)}
            columns={columns}
            data={data}
            emptyDescription={emptyDescription}
            emptyTitle={emptyTitle}
            isLoading={isLoading}
            loadingState={<DataTableSkeleton columns={columns.length} rows={5} />}
            onView={canView && onView ? (_id, row) => row && onView(row) : undefined}
            onEdit={canEdit && onUpdate ? openEditDrawer : undefined}
            onRemove={
              canRemove && onRemove
                ? (_, row) => {
                    if (!row) {
                      return;
                    }

                    if (window.confirm("Remove this record? This action cannot be undone.")) {
                      deleteMutation.mutate(row);
                    }
                  }
                : undefined
            }
            rowKey={rowKey}
            toolbar={toolbar}
          />
        )}
      </ContentSlot>

      {helperCard && helperCardPlacement === "below" ? (
        <HelperCardSlot $tight={embedded}>{helperCard}</HelperCardSlot>
      ) : null}

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRow(null);
        }}
        title={
          mode === "edit" ? drawerTitleEdit || `Edit ${title}` : drawerTitleCreate || createLabel
        }
      >
        {renderForm ? renderForm(formProps) : <CrudForm {...formProps} />}
      </RightDrawer>
    </>
  );
};
